import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnChanges, Output } from '@angular/core';
import { DxDateBoxModule, DxSelectBoxModule } from 'devextreme-angular';
import {
  REPORT_PERIODS,
  ReportPeriod,
  ReportPeriodSelection,
  ReportPeriodOption,
  buildReportPeriodOptions,
  dateOnly,
  formatReportDate,
  reportAvailabilityMessage,
  reportRangeErrors,
  resolveReportPeriod,
  selectReportPeriod,
} from '../../utils/report-period.utils';

@Component({
  selector: 'ag-shr-report-period-control',
  standalone: true,
  imports: [DxDateBoxModule, DxSelectBoxModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="report-period-control">
      <dx-select-box
        label="Period"
        [labelMode]="showLabel ? 'floating' : 'hidden'"
        [inputAttr]="{ 'aria-label': 'Period' }"
        [items]="periodItems"
        valueExpr="value"
        displayExpr="description"
        [value]="selection.period"
        [disabled]="!through"
        (onValueChanged)="changePeriod($event.value)"></dx-select-box>
      @if (selection.period === 'ytd') {
        <dx-date-box
          label="Through date"
          labelMode="floating"
          type="date"
          displayFormat="MM/dd/yyyy"
          dateSerializationFormat="yyyy-MM-dd"
          [value]="selection.periodEnding || null"
          [min]="through ? through.slice(0, 4) + '-01-01' : null"
          [max]="through"
          [isValid]="!endDateErrors.length"
          [validationErrors]="endDateErrors"
          validationMessageMode="auto"
          [disabled]="!through"
          [useMaskBehavior]="true"
          (onValueChanged)="changeYtdEnding($event.value)"></dx-date-box>
      } @else if (selection.period !== 'custom') {
        <dx-select-box
          label="Period Ending"
          labelMode="floating"
          [items]="options"
          valueExpr="value"
          displayExpr="description"
          [value]="selection.periodEnding"
          [disabled]="!through"
          [searchEnabled]="true"
          noDataText="No completed periods available"
          (onValueChanged)="changeEnding($event.value)"></dx-select-box>
      } @else if (showCustomDates) {
        <dx-date-box
          label="Start Date *"
          labelMode="floating"
          type="date"
          displayFormat="MM/dd/yyyy"
          dateSerializationFormat="yyyy-MM-dd"
          [value]="selection.startDate || null"
          [max]="through"
          [isValid]="!startDateErrors.length"
          [validationErrors]="startDateErrors"
          validationMessageMode="auto"
          [inputAttr]="{ 'aria-required': 'true' }"
          [disabled]="!through"
          [useMaskBehavior]="true"
          (onValueChanged)="changeDate('startDate', $event.value)"></dx-date-box>
        <dx-date-box
          label="End Date *"
          labelMode="floating"
          type="date"
          displayFormat="MM/dd/yyyy"
          dateSerializationFormat="yyyy-MM-dd"
          [value]="selection.endDate || null"
          [isValid]="!endDateErrors.length"
          [validationErrors]="endDateErrors"
          validationMessageMode="auto"
          [inputAttr]="{ 'aria-required': 'true' }"
          [max]="through"
          [disabled]="!through"
          [useMaskBehavior]="true"
          (onValueChanged)="changeDate('endDate', $event.value)"></dx-date-box>
      }
      @if (showCustomDates) {
        <p><strong>Report Range:</strong> {{ rangeLabel }}</p>
      }
      <p class="report-period-control__availability" role="status">
        <i class="dx-icon dx-icon-warning" aria-hidden="true"></i>
        <span>{{ availabilityLabel }}</span>
      </p>
    </div>
  `,
  styles: [
    `
      :host {
        display: block;
      }
      .report-period-control {
        display: grid;
        gap: 12px;
        margin: 12px 0;
      }
      .report-period-control__availability {
        display: flex;
        align-items: baseline;
        gap: 6px;
      }
      .report-period-control__availability .dx-icon {
        font-size: 13px;
      }
      p {
        margin: 0;
        font-size: 13px;
      }
    `,
  ],
})
export class ReportPeriodControlComponent implements OnChanges {
  @Input() selection: ReportPeriodSelection = selectReportPeriod(false, 'custom');
  @Input() paid = false;
  @Input() through: string | null = null;
  @Input() from: string | null = null;
  @Input() showCustomDates = true;
  /** Session creation still uses its four API-supported period modes. */
  @Input() includeYtd = true;
  @Input() showLabel = true;
  @Output() readonly selectionChange = new EventEmitter<ReportPeriodSelection>();
  protected periodItems: { value: ReportPeriod; description: string; disabled: boolean }[] = [];
  protected options: ReportPeriodOption[] = [];
  protected startDateErrors: { message: string }[] = [];
  protected endDateErrors: { message: string }[] = [];
  protected rangeLabel = 'Not set';
  protected availabilityLabel = '';
  ngOnChanges(): void {
    this.options = buildReportPeriodOptions(this.selection.period, this.through, undefined, this.from);
    this.periodItems = REPORT_PERIODS.filter(p => this.includeYtd || p.value !== 'ytd').map(p => ({
      ...p,
      disabled:
        !this.through ||
        (p.value !== 'custom' && !buildReportPeriodOptions(p.value, this.through, undefined, this.from).length),
    }));
    const errors = reportRangeErrors(this.selection, this.through);
    this.startDateErrors = errors
      .filter(
        message =>
          message.startsWith('Start Date') ||
          (message.startsWith('Data is only available') &&
            !!dateOnly(this.selection.startDate) &&
            this.selection.startDate > this.through!),
      )
      .map(message => ({ message }));
    this.endDateErrors = errors
      .filter(
        message =>
          message.startsWith('End Date') ||
          (message.startsWith('Data is only available') &&
            !!dateOnly(this.selection.endDate) &&
            this.selection.endDate > this.through!),
      )
      .map(message => ({ message }));
    this.rangeLabel =
      dateOnly(this.selection.startDate) && dateOnly(this.selection.endDate)
        ? `${formatReportDate(this.selection.startDate)}–${formatReportDate(this.selection.endDate)}`
        : 'Not set';
    this.availabilityLabel = reportAvailabilityMessage(this.selection.period, this.through);
  }
  protected changePeriod(period: ReportPeriod): void {
    if (period === this.selection.period || !this.periodItems.some(item => item.value === period && !item.disabled))
      return;
    if (period === 'custom') {
      this.selectionChange.emit(resolveReportPeriod(this.paid, this.through, { period: 'custom' }, this.from));
      return;
    }
    const end = buildReportPeriodOptions(period, this.through, undefined, this.from)[0]?.value ?? '';
    this.selectionChange.emit(selectReportPeriod(this.paid, period, end));
  }
  protected changeYtdEnding(value: unknown): void {
    const end = dateOnly(value) ?? '';
    if (end === this.selection.periodEnding) return;
    this.selectionChange.emit(selectReportPeriod(this.paid, 'ytd', end));
  }
  protected changeEnding(end: string): void {
    if (end === this.selection.periodEnding || !this.options.some(o => o.value === end)) return;
    this.selectionChange.emit(selectReportPeriod(this.paid, this.selection.period, end));
  }
  protected changeDate(key: 'startDate' | 'endDate', value: unknown): void {
    const date = dateOnly(value) ?? '';
    if (date === this.selection[key]) return;
    this.selectionChange.emit({ ...this.selection, [key]: date });
  }
}
